import React, { FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { FCP, TAutosuggestPayload } from 'types';

import { PopupCloseIcon } from 'components/svg';
import ZpButton from 'components/zpButton';
import { useTracking } from 'hooks';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import * as reviewService from 'services/jobReviewService';
import { getReviewsByEntity } from 'services/userService';
import {
  goToNextSection,
  setAllQuestionsSubmitted,
  setQuestionAnswerMap,
  setUpdateReviews,
  submitQuestionAnswer,
} from 'store/companyReviews';
import {
  COMPANY_REVIEW_QUESTION_IDS,
  COMPANY_REVIEW_RATING_CATEGORIES,
  REVIEW_FORM_LS_KEY,
  TRACKING,
} from 'utils/constant';

import { importStatic } from '../../utils';

import CompanyAnonReviewFormSection from './CompanyAnonReviewFormSection';
import CompanyAnonReviewSuccess from './CompanyAnonReviewSuccess';
import GuidelinesTooltip from './guidelinesTooltip';
import { IFormattedResponse, IQuestionAnswerMap, IReviewPayload, QType } from './types';

import classes from './companyAnonReviewForm.module.css';

const AutosuggestInput = dynamic(() => import('components/autosuggestInput'), { ssr: false });

export interface CompanyAnonReviewFormProps extends FCP {
  companyID: number;
  companyName: string;
  popup?: boolean;
  className?: string;
  onPopupClose?(): void;
  onPopupCloseClick?(): void;
  companyInputCallback?(autoSuggestProps: TAutosuggestPayload): void;
  zippiDimensions?: {
    width: number;
    height: number;
  };
  onRatingChange?(rating: number, questionId?: string): void;
  maxCharLength?: number;
  shareText?: string | JSX.Element;
  reviewCategory?: string;
  source?: string;
  surveyId?: string;
  hideGuidelines?: boolean;
  lastSectionIndex?: number;
  popupHeaderTitle?: string | JSX.Element;
  disableNextButton?: boolean;
  popupHeaderClassName?: string;
  signUpSurvey?: boolean;
  /**
   * @param questionAnswerMap A map of question and answer for questions for the current sections.
   * Answer will be empty for skip case
   */
  onSkip?(questionAnswerMap?: IQuestionAnswerMap): void;
  /**
   * @param questionAnswerMap A map of question and answer for questions for the current sections.
   * Answers will not empty in this case.
   */
  onNext?(questionAnswerMap?: IQuestionAnswerMap): void;
  /**
   * @param questionAnswerMap A map of question and answer for questions for the current sections.
   * Answers will not be empty in this case as well but this callback will be called once the reviews are successfully submitted.
   */
  onReviewFormSubmitComplete?(questionAnswerMap?: IQuestionAnswerMap): void;
}

const MAX_CHAR_LENGTH = 75;
const DEFAULT_ENTITY_TYPE = 'COMPANY';
const DEFAULT_SHARE_TEXT = 'Share your Experience';

const CompanyAnonReviewForm = ({
  popup,
  companyName,
  companyID,
  zippiDimensions,
  hideGuidelines,
  onSkip,
  onNext,
  onPopupClose,
  onPopupCloseClick,
  onRatingChange,
  companyInputCallback,
  onReviewFormSubmitComplete,
  className = '',
  lastSectionIndex = 4,
  popupHeaderTitle = null,
  popupHeaderClassName = '',
  disableNextButton = false,
  signUpSurvey = false,
  shareText = DEFAULT_SHARE_TEXT,
  maxCharLength = MAX_CHAR_LENGTH,
  reviewCategory = 'USER_REVIEWS',
  source = 'company-reviews-page',
  surveyId = '5ab14651c19fe54c627a14ca',
}: CompanyAnonReviewFormProps) => {
  // default payload to be sent for the company reviews
  const defaultPayload = {
    entityType: DEFAULT_ENTITY_TYPE,
    entityValue: companyID ? String(companyID) : companyName,
    reviewCategory,
    source: source,
    surveyId: surveyId,
  };

  const tracking = useTracking();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(({ user }) => user.userId);
  const { questionAnswerMap, currentSectionIndex, updateReviews, allQuestionsSubmitted } =
    useAppSelector((state) => state.companyReviews);

  // review questions for all the sections
  const sectionQuestions = useMemo(
    () => [
      // section 1
      {
        questionType: QType.five_star_select,
        defaultErrorMessage: '',
        questions: COMPANY_REVIEW_RATING_CATEGORIES.map(({ key, value, id }) => ({
          id,
          key,
          label: `${value}*`,
          placeholder: '',
        })),
      },
      // section 2
      {
        questionType: QType.textbox,
        defaultErrorMessage: `Please enter at least ${maxCharLength} characters in these fields`,
        questions: [
          {
            id: COMPANY_REVIEW_QUESTION_IDS.pros,
            key: 'pros',
            label: `Pros of working at ${companyName}`,
            placeholder: `I thought the pros of working at ${companyName} are...`,
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.cons,
            key: 'cons',
            label: `Cons of working at ${companyName}`,
            placeholder: `In my experience, the cons of working at ${companyName} are...`,
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.benefits,
            key: 'benefits',
            label: `What's Your Favorite Perk / Benefit?`,
            placeholder: `My favorite benefits at ${companyName} are...`,
          },
        ],
      },
      // section 3
      {
        questionType: QType.textbox,
        defaultErrorMessage: `Please enter at least ${maxCharLength} characters in these fields`,
        questions: [
          {
            id: COMPANY_REVIEW_QUESTION_IDS.ceo,
            key: 'ceo',
            label: 'What do you like best about the CEO and the leadership team?',
            placeholder: `What I like best about the leadership at ${companyName} is...`,
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.management,
            key: 'management',
            label: 'How would you improve the company culture?',
            placeholder: `I would improve ${companyName} by...`,
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.interview,
            key: 'interview',
            label: 'How did you prepare for an interview?',
            placeholder: `I prepared for an interview at ${companyName} by...`,
          },
        ],
      },
      // section 4
      {
        questionType: QType.textbox,
        defaultErrorMessage: `Please enter at least ${maxCharLength} characters in these fields`,
        questions: [
          {
            id: COMPANY_REVIEW_QUESTION_IDS.compensation,
            key: 'compensation',
            label: `How does your compensation at ${companyName} compare to the industry average?`,
            placeholder: `In my experience, the compensation at ${companyName} when compared to the industry average is...`,
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.diversity,
            key: 'diversity',
            label:
              'How do you feel about the representation of diverse demographics within your organization?',
            placeholder: `The representation of diverse demographics at ${companyName} is...`,
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.joy,
            key: 'joy',
            label: 'What brings you the most joy at work?',
            placeholder: `Personally, the most fulfilling aspect of my work at ${companyName} is...`,
          },
        ],
      },
      // section 5
      {
        questionType: QType.autosuggest,
        defaultErrorMessage: 'Both Job Title and Location are required',
        questions: [
          {
            id: COMPANY_REVIEW_QUESTION_IDS.title,
            key: 'title',
            label: `Your Job Title When You Worked At ${companyName}`,
            placeholder: '',
          },
          {
            id: COMPANY_REVIEW_QUESTION_IDS.location,
            key: 'location',
            label: 'Office Location',
            placeholder: '',
          },
        ],
      },
    ],
    [maxCharLength, companyName]
  );

  // this represents the current section of all the review sections
  const currentSection = sectionQuestions[currentSectionIndex];

  const { defaultErrorMessage, questionType, questions: currentQuestions } = currentSection || {};

  const [sectionsValidity, setSectionsValidity] = useState(sectionQuestions.map(() => false));
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [textBasedQuestionAnswerMap, setTextBasedQuestionAnswerMap] = useState(questionAnswerMap);

  const questionAnswerMapRef = useRef(questionAnswerMap);

  // if the current section questions are text based
  const isTextBasedQuestionsSection = useMemo(() => {
    return questionType === QType.textbox;
  }, [questionType]);

  const currentQuestionAnswerMap = useMemo<IQuestionAnswerMap>(() => {
    const answersMap = isTextBasedQuestionsSection ? textBasedQuestionAnswerMap : questionAnswerMap;
    return currentQuestions.reduce((acc, question) => {
      acc[question.id] = answersMap?.[question.id] || '';
      return acc;
    }, {});
  }, [
    currentQuestions,
    isTextBasedQuestionsSection,
    textBasedQuestionAnswerMap,
    questionAnswerMap,
  ]);

  const responses = useMemo(() => {
    return Object.entries({ ...questionAnswerMap, ...currentQuestionAnswerMap })
      .map(([questionId, answer]) => ({
        questionId,
        answers: [answer],
      }))
      .filter(({ answers }) => answers?.length > 0);
  }, [questionAnswerMap, currentQuestionAnswerMap]);

  const completeReviewPayload: IReviewPayload = useMemo(
    () => ({
      ...defaultPayload,
      responses,
      updateReviews,
    }),
    [responses, updateReviews, defaultPayload.entityValue]
  );

  // make the current section dirty if one of the question for the current section was changed
  const isCurrentSectionDirty = useMemo(
    () => currentQuestions.some((question) => question.id in currentQuestionAnswerMap),
    [currentQuestions, currentQuestionAnswerMap]
  );

  // a boolean flag for checking validity of the current section
  const isCurrentSectionValid = useMemo(() => {
    switch (questionType) {
      case QType.autosuggest:
        return currentQuestions.every(({ id }) => currentQuestionAnswerMap[id]?.length > 0);
      case QType.five_star_select:
        return currentQuestions.every(({ id }) => currentQuestionAnswerMap[id]?.length > 0);
      case QType.textbox:
        return (
          currentQuestions?.reduce(
            (count, { id }) => count + (currentQuestionAnswerMap[id]?.length || 0),
            0
          ) >= maxCharLength
        );
      default:
        return false;
    }
  }, [currentQuestions, questionType, currentQuestionAnswerMap]);

  useEffect(() => {
    setSectionsValidity((validity) =>
      validity.map((_, index) => {
        if (index === currentSectionIndex) {
          return isCurrentSectionValid;
        } else {
          return validity[index] || false;
        }
      })
    );
  }, [currentSectionIndex, isCurrentSectionValid]);

  // run this hook to fetch already submitted reviews by the user either partially or fully
  useEffect(() => {
    const fetchReviewsByEntity = async () => {
      try {
        const reviewResponses = await getReviewsByEntity(
          defaultPayload.entityType,
          defaultPayload.entityValue,
          defaultPayload.reviewCategory
        );
        const allQuestions = [
          ...COMPANY_REVIEW_RATING_CATEGORIES.map((question) => question.id),
          ...Object.values(COMPANY_REVIEW_QUESTION_IDS),
        ];
        dispatch(
          setAllQuestionsSubmitted(
            reviewResponses?.length === allQuestions?.length &&
              reviewResponses?.every(({ moderatedAnswers }) => moderatedAnswers?.[0]?.length > 0)
          )
        );
        if (reviewResponses?.length) {
          dispatch(setUpdateReviews(true));
          dispatch(
            setQuestionAnswerMap(
              reviewResponses?.reduce((acc, { questionId, moderatedAnswers }) => {
                acc[questionId] = moderatedAnswers?.[0] || '';
                return acc;
              }, {}) || {}
            )
          );
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (userId && defaultPayload.entityValue && allQuestionsSubmitted === null) {
      fetchReviewsByEntity();
    }
  }, [userId, defaultPayload.entityValue, allQuestionsSubmitted]);

  // listen to review submit event and make an API call to record the responses of the reviews
  const handleSubmitReviewResponses = async (
    reviewPayload: IReviewPayload = completeReviewPayload,
    incomplete = false
  ) => {
    try {
      setIsReviewSubmitting(true);
      const response = await reviewService.reviewResponse(reviewPayload);
      if (response?.data?.success) {
        tracking.logActivity({}, TRACKING.LOG_ACTIVITY.COMPANY_REVIEW_SUBMITTED);
        dispatch(setAllQuestionsSubmitted(incomplete ? sectionsValidity.every(Boolean) : true));
        dispatch(setQuestionAnswerMap(questionAnswerMapRef.current));

        // Save when this review happened to prevent the global company review popup
        // from showing up again before this review is approved.
        localStorage.setItem(
          REVIEW_FORM_LS_KEY.RECENT_REVIEWS,
          JSON.stringify([
            ...JSON.parse(localStorage.getItem(REVIEW_FORM_LS_KEY.RECENT_REVIEWS) || '[]'),
            {
              companyID,
              time: new Date().toISOString(),
            },
          ])
        );
      }
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  // listen to brower window close, tab close event to submit the incomplete reviews submitted by the user
  const submitIncompleteReviewResponses = useCallback(async () => {
    if (defaultPayload.entityValue && !allQuestionsSubmitted) {
      const userResponses = questionAnswerMapRef.current || {};
      const filteredResponses: IFormattedResponse[] = Object.keys(userResponses)
        .map((questionId) => ({
          questionId,
          answers: [userResponses[questionId]],
        }))
        .filter(({ answers }) => answers?.[0]?.length);

      if (filteredResponses?.length) {
        const reviewPayload = {
          ...defaultPayload,
          responses: filteredResponses,
          updateReviews,
        };

        handleSubmitReviewResponses(reviewPayload, true);
      }
    }
  }, [allQuestionsSubmitted, defaultPayload.entityValue, updateReviews]);

  useEffect(() => {
    // Listen to beforeunload event when either
    // the browser or the it's tab is being closed
    window.addEventListener('beforeunload', submitIncompleteReviewResponses);

    return () => {
      window.removeEventListener('beforeunload', submitIncompleteReviewResponses);
    };
  }, [submitIncompleteReviewResponses]);

  // listen to change event of question and record the answers
  const onSubmitAnswer = (questionId: string, answer = '') => {
    questionAnswerMapRef.current = {
      ...(questionAnswerMapRef.current || {}),
      [questionId]: answer,
    };
    if (isTextBasedQuestionsSection) {
      setTextBasedQuestionAnswerMap((prevMap) => ({
        ...prevMap,
        [questionId]: answer,
      }));
    } else {
      dispatch(submitQuestionAnswer({ questionId, answer }));
    }
  };

  const handleNext = (nextFun = null) => {
    if (nextFun && typeof nextFun === 'function') {
      nextFun(currentQuestionAnswerMap);
    } else if (currentSectionIndex < lastSectionIndex) {
      dispatch(goToNextSection());
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip(currentQuestionAnswerMap);
    } else {
      dispatch(goToNextSection());
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (currentSectionIndex === lastSectionIndex) {
      // submit the responses
      try {
        await handleSubmitReviewResponses();
      } catch {
      } finally {
        handleNext(onReviewFormSubmitComplete);
      }
    } else {
      // move to the next section
      if (isTextBasedQuestionsSection) {
        dispatch(setQuestionAnswerMap(textBasedQuestionAnswerMap));
      }
      handleNext(onNext);
    }
  };

  const sectionClasses = (() => {
    switch (questionType) {
      case QType.five_star_select:
        return {
          className: classNames('row gg-16', { ['mx-0']: !popup }),
          questionClassName: classNames(
            'd-flex justify-content-between align-items-center col-12 row',
            {
              ['col-md-6']: popup,
              ['mx-0 px-0']: !popup,
            }
          ),
          labelClassName: classNames('mb-0 col-6', { ['px-0']: !popup }),
        };
      case QType.textbox:
        return {
          className: 'd-flex flex-column gg-16',
          questionClassName: classNames('d-flex flex-column', classes.textBasedQuestion),
        };
      default:
        return {
          className: 'd-flex flex-column gg-16',
        };
    }
  })();

  const successTitles = (() => ({
    title: 'Review Received',
    subTitle: (
      <p className="mb-0 ml-3">
        Thanks for helping others find the right company.{' '}
        <a href="/company/review/" className="z-link z-font-600">
          Help us with another company!
        </a>
      </p>
    ),
  }))();
  const renderForm = () => {
    return (
      <div
        id="companyReviewForm"
        className={classNames(
          popup ? '' : 'rounded',
          popup ? classes['form-container-popup'] : classes['form-container'],
          signUpSurvey ? classes['form-container-survey-step'] : 'z-p-20',
          className
        )}
      >
        {allQuestionsSubmitted ? (
          <CompanyAnonReviewSuccess
            {...successTitles}
            className="submitEnd"
            titleClassName="z-font-600 z-font-18 text-center mb-0"
            subTitleClassName={classNames(
              'd-flex flex-column-reverse flex-md-row align-items-center z-mt-18',
              classes['zippi']
            )}
          />
        ) : (
          <form onSubmit={handleSubmit}>
            {questionType === QType.five_star_select && (
              <>
                {!hideGuidelines && (
                  <div className={classNames('d-flex align-items-center mb-4', classes['zippi'])}>
                    <img
                      src={importStatic('/ui-router/images/footer/zippi-homepage.svg')}
                      width={zippiDimensions?.width || 100}
                      height={zippiDimensions?.height || 100}
                    />
                    <div className={classNames('z-px-15 z-py-16 z-ml-10', classes.zippiMessage)}>
                      <span className="z-mr-6">{shareText}</span>
                      <GuidelinesTooltip />
                    </div>
                  </div>
                )}

                {companyInputCallback && (
                  <AutosuggestInput
                    category="company"
                    prefillValue={companyName || ''}
                    inputProps={{
                      className: 'form-control mb-3 z-h-48',
                      name: 'company',
                      placeholder: 'Company you work(ed) for',
                    }}
                    autoFocus
                    onChange={companyInputCallback}
                    surveyAutoAccept
                    closeOverlayOnBlur
                  />
                )}
              </>
            )}

            <CompanyAnonReviewFormSection
              questions={currentQuestions}
              questionType={questionType}
              onSubmitAnswer={(questionId, answer) => {
                if (questionType === QType.five_star_select) {
                  onRatingChange?.(+answer, questionId);
                }
                onSubmitAnswer(questionId, answer);
              }}
              questionAnswerMap={currentQuestionAnswerMap}
              {...sectionClasses}
            />

            {isCurrentSectionDirty && !isCurrentSectionValid && defaultErrorMessage && (
              <div className="show-error z-mb-18">
                <span className="error">{defaultErrorMessage}</span>
              </div>
            )}

            <div
              className={`d-flex justify-content-center ${
                signUpSurvey ? classes['signUpFooter'] : ''
              }`}
            >
              {onSkip && (
                <ZpButton
                  size="big"
                  isFullWidth
                  type="button"
                  theme="link"
                  className="z-mt-20"
                  onClick={handleSkip}
                  style={{ flex: 1 }}
                >
                  Skip
                </ZpButton>
              )}
              <ZpButton
                size="big"
                isFullWidth
                type="submit"
                className="z-mt-20"
                style={{ flex: 2 }}
                disabled={!isCurrentSectionValid || disableNextButton || isReviewSubmitting}
                theme={currentSectionIndex === lastSectionIndex ? 'super-primary' : 'primary'}
              >
                {currentSectionIndex === lastSectionIndex ? 'Submit Anonymous Review' : 'Next'}
              </ZpButton>
            </div>
          </form>
        )}
      </div>
    );
  };

  if (popup) {
    return (
      <>
        <div
          className={classNames(
            classes['popup-header'],
            'd-flex justify-content-end align-items-center z-p-15',
            popupHeaderClassName
          )}
        >
          {popupHeaderTitle}
          <PopupCloseIcon
            onClick={
              onPopupCloseClick ||
              (() => {
                submitIncompleteReviewResponses();
                onPopupClose?.();
              })
            }
          />
        </div>
        {renderForm()}
      </>
    );
  } else {
    return renderForm();
  }
};

export default memo(CompanyAnonReviewForm);
